import React, { useState, useMemo } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { doc, updateDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useCompany } from '../../../context/CompanyContext';
import { sanitizeForFirestore } from '../../../utils/firestoreUtils';
import { toast } from 'sonner';
import ClientRow from './ClientRow';
import ClientCardMobile from './ClientCardMobile';
import TableToolbar from './TableToolbar';
import Pagination from './Pagination';
import BulkActionToolbar from './BulkActionToolbar';
import { matchesQuery } from '../../../utils/contactSearch';

/**
 * ClientTable - Enhanced DataTable with sorting, filtering, search, pagination
 */
const ClientTable = ({
  clients,
  loading,
  error,
  // New: hook-managed server-side search + pagination. When provided,
  // the local `searchTerm` state is treated as a driver for the parent
  // hook (which owns the actual full-dataset fetch); local filtering
  // still runs so stage filter + sort work over whatever page/search
  // result the parent supplied. Backwards-compatible: if not passed,
  // the old client-side-only behavior remains.
  externalSearchTerm,
  onSearchTermChange,
  isSearching,
  hasMore,
  loadMore,
  loadingMore,
}) => {
  const { companyId } = useCompany();
  // Table state
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const usingExternalSearch = typeof onSearchTermChange === 'function';
  const searchTerm = usingExternalSearch ? (externalSearchTerm || '') : localSearchTerm;
  const setSearchTerm = usingExternalSearch ? onSearchTermChange : setLocalSearchTerm;
  const [stageFilter, setStageFilter] = useState('');
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  const [visibleColumns, setVisibleColumns] = useState({
    name:    true,
    address: true,
    phone:   true,
    email:   true,
    stage:   true,
    card:    true,
    created: true,
    actions: true,
  });

  // Get unique stages for filter
  const availableStages = useMemo(() => {
    const stages = new Set(clients.map(c => c.stage));
    return Array.from(stages).sort();
  }, [clients]);

  // Handle sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Search and filter clients.
  //
  // When `usingExternalSearch` is true, the parent hook already
  // performed a full-dataset server-side search — the `clients`
  // prop is the authoritative result set, so we skip the local
  // `matchesQuery` filter (it would only re-narrow a set that's
  // already narrow, and is not needed for stage matching since
  // server search doesn't index stage). Stage filter still applies
  // as a local narrow.
  const filteredClients = useMemo(() => {
    let result = [...clients];

    if (searchTerm && !usingExternalSearch) {
      result = result.filter((client) =>
        matchesQuery(client, searchTerm, { extraTextFields: ['stage'] })
      );
    }

    // Apply stage filter
    if (stageFilter) {
      result = result.filter((client) => client.stage === stageFilter);
    }

    return result;
  }, [clients, searchTerm, stageFilter, usingExternalSearch]);

  // Sort clients
  const sortedClients = useMemo(() => {
    const sorted = [...filteredClients];

    sorted.sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'stage':
          aValue = (a.stage || '').toLowerCase();
          bValue = (b.stage || '').toLowerCase();
          break;
        case 'address':
          aValue = (a.address || '').toLowerCase();
          bValue = (b.address || '').toLowerCase();
          break;
        case 'createdAt':
          aValue = a.createdAt?.toMillis?.() || 0;
          bValue = b.createdAt?.toMillis?.() || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredClients, sortColumn, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(sortedClients.length / pageSize);
  const paginatedClients = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return sortedClients.slice(start, end);
  }, [sortedClients, currentPage, pageSize]);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, stageFilter, pageSize]);

  // Clear selections when visible dataset changes
  React.useEffect(() => {
    setSelectedIds([]);
  }, [searchTerm, stageFilter, sortColumn, sortDirection, currentPage, pageSize]);

  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(paginatedClients.map(c => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  // Sprint 292 — Lifecycle remediation. The former `handleBulkDelete` (raw
  // `batch.delete(leads/{id})`) has been removed. Bulk destruction is now
  // "Close Accounts", which calls the S4 lifecycle endpoint per selection —
  // preserving history and cancelling future recurring work. Hard purge is
  // an owner-only backend workflow and is NEVER exposed as a bulk action.
  const handleBulkCloseAccounts = async () => {
    const count = selectedIds.length;
    const confirmed = window.confirm(
      `Close ${count} account${count > 1 ? 's' : ''}? This stops recurring service and cancels future visits, but preserves service history, quotes, invoices, and payment records. This is reversible.`
    );

    if (!confirmed) return;

    let ok = 0;
    let already = 0;
    let failed = 0;
    for (const leadId of selectedIds) {
      try {
        const idToken = await (await import('../../../firebase')).auth.currentUser?.getIdToken();
        const resp = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/leads/${leadId}/account/close`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              reason: 'other',
              note: 'Bulk close from Clients table',
              cancelFutureVisits: true,
            }),
          }
        );
        if (resp.ok) {
          ok += 1;
        } else if (resp.status === 409) {
          already += 1; // already closed — idempotent
        } else {
          failed += 1;
        }
      } catch {
        failed += 1;
      }
    }

    if (failed === 0) {
      toast.success(
        `Closed ${ok} account${ok !== 1 ? 's' : ''}${already ? ` (${already} already closed)` : ''}`
      );
    } else {
      toast.error(`Closed ${ok}, failed ${failed}${already ? `, ${already} already closed` : ''}`);
    }
    setSelectedIds([]);
  };

  // Bulk stage change handler
  const handleBulkStageChange = async (newStage) => {
    const count = selectedIds.length;

    try {
      const { chunkedBatch } = await import('../../../lib/firestoreBatch');
      await chunkedBatch(db, selectedIds, (batch, id) => {
        batch.update(
          doc(db, 'companies', companyId, 'leads', id),
          sanitizeForFirestore({
            stage: newStage,
            updatedAt: serverTimestamp(),
          })
        );
      });
      
      toast.success(`Stage updated to "${newStage}" for ${count} client${count > 1 ? 's' : ''}`);
      setSelectedIds([]);
    } catch (error) {
      console.error('Error changing stage:', error);
      toast.error('Failed to change stage');
      throw error;
    }
  };

  // Column toggle handler
  const handleColumnToggle = (columnId) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  // Render sort icon
  const renderSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? (
      <ArrowUp className="h-4 w-4 inline ml-1" />
    ) : (
      <ArrowDown className="h-4 w-4 inline ml-1" />
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading clients...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <p className="text-red-600 font-medium">Error loading clients</p>
          <p className="text-sm text-gray-500 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  if (!clients || clients.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <p className="text-gray-600 font-medium">No clients found</p>
          <p className="text-sm text-gray-500 mt-1">
            Clients will appear here once they're added to your system.
          </p>
        </div>
      </div>
    );
  }

  const allSelected = paginatedClients.length > 0 && selectedIds.length === paginatedClients.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < paginatedClients.length;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Toolbar */}
      <TableToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedCount={selectedIds.length}
        onBulkCloseAccounts={handleBulkCloseAccounts}
        visibleColumns={visibleColumns}
        onColumnToggle={handleColumnToggle}
        stageFilter={stageFilter}
        onStageFilterChange={setStageFilter}
        availableStages={availableStages}
      />

      {/* Bulk Action Toolbar */}
      <BulkActionToolbar
        selectedCount={selectedIds.length}
        onBulkCloseAccounts={handleBulkCloseAccounts}
        onBulkStageChange={handleBulkStageChange}
        availableStages={availableStages}
        isProcessing={false}
      />

      {/* ── Mobile card list (< lg) ───────────────────────────────────────── */}
      <div className="lg:hidden divide-y divide-gray-100" data-testid="clients-mobile-list">
        {paginatedClients.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">
            No clients match your search
          </div>
        ) : (
          paginatedClients.map((client) => (
            <ClientCardMobile key={client.id} client={client} />
          ))
        )}
      </div>

      {/* ── Desktop table (≥ lg) ─────────────────────────────────────────── */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
            <tr>
              {/* Select all checkbox */}
              <th className="px-4 py-3 w-12">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={input => {
                    if (input) {
                      input.indeterminate = someSelected;
                    }
                  }}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>

              {/* Name - sortable */}
              {visibleColumns.name && (
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  Name {renderSortIcon('name')}
                </th>
              )}

              {/* Address - sortable */}
              {visibleColumns.address && (
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('address')}
                >
                  Address {renderSortIcon('address')}
                </th>
              )}

              {/* Phone */}
              {visibleColumns.phone && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone
                </th>
              )}

              {/* Email */}
              {visibleColumns.email && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
              )}

              {/* Stage - sortable */}
              {visibleColumns.stage && (
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('stage')}
                >
                  Stage {renderSortIcon('stage')}
                </th>
              )}

              {/* Card on File */}
              {visibleColumns.card && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Card on File
                </th>
              )}

              {/* Created - sortable */}
              {visibleColumns.created && (
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('createdAt')}
                >
                  Created {renderSortIcon('createdAt')}
                </th>
              )}

              {/* Actions */}
              {visibleColumns.actions && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedClients.map((client) => (
              <ClientRow
                key={client.id}
                client={client}
                isSelected={selectedIds.includes(client.id)}
                onSelect={handleSelectOne}
                visibleColumns={visibleColumns}
              />
            ))}
          </tbody>
        </table>
      </div>
      {/* end desktop table */}

      {/* Pagination */}
      {sortedClients.length > 0 && !usingExternalSearch && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={sortedClients.length}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {/* Cursor-paged "Load more" — active when the parent hook drives
          fetches (server-side full-dataset flow). Hidden during search
          (server returns bounded top-N and doesn't paginate further). */}
      {usingExternalSearch && !isSearching && hasMore && (
        <div className="flex justify-center py-4">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="text-sm font-medium text-white px-4 py-2 rounded-md disabled:opacity-60"
            style={{ backgroundColor: 'var(--cb-orange)' }}
            data-testid="clients-load-more-btn"
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ClientTable;
